import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";

// Widget de ayuda flotante conectado a un workflow de n8n (Chat Trigger).
// Se activa poniendo la URL pública del Chat Trigger en REACT_APP_N8N_CHAT_URL.
const N8N_URL = process.env.REACT_APP_N8N_CHAT_URL || "";

function newSession() {
  let s = localStorage.getItem("aios_help_session");
  if (!s) {
    s = "web-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("aios_help_session", s);
  }
  return s;
}

export default function HelpChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "bot", text: "¡Hola! Soy el asistente de Agent Company OS. ¿En qué te ayudo?" },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scroller = useRef(null);
  const session = useRef(newSession());

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [msgs, open]);

  // Si no hay URL configurada, el widget no se muestra.
  if (!N8N_URL) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const res = await fetch(N8N_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "sendMessage", chatInput: text, sessionId: session.current }),
      });
      let reply = "";
      try {
        const data = await res.json();
        reply = data.output ?? data.text ?? data.message ?? data.reply ??
                (typeof data === "string" ? data : JSON.stringify(data));
      } catch {
        reply = await res.text();
      }
      setMsgs((m) => [...m, { role: "bot", text: reply || "…" }]);
    } catch {
      setMsgs((m) => [...m, { role: "bot", text: "Ahora mismo no puedo responder. Inténtalo en un momento." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105"
        style={{ background: "var(--accent)", color: "var(--bg)" }}
        aria-label="Ayuda"
        data-testid="help-chat-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col border shadow-2xl"
          style={{ background: "var(--surface)", borderColor: "var(--border)" }}
          data-testid="help-chat-panel"
        >
          <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center"
                 style={{ background: "var(--accent)", color: "var(--bg)" }}>
              <MessageCircle className="w-4 h-4" />
            </div>
            <div>
              <div className="font-display font-bold text-sm text-white leading-none">Asistente</div>
              <div className="label-mono mt-1">Agent Company OS</div>
            </div>
          </div>

          <div ref={scroller} className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className="max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap"
                  style={m.role === "user"
                    ? { background: "var(--accent)", color: "var(--bg)" }
                    : { background: "var(--surface-2)", color: "var(--text)" }}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && <div className="label-mono text-[var(--text-dim)]">escribiendo…</div>}
          </div>

          <div className="p-3 border-t flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Escribe tu duda…"
              className="flex-1 bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm text-white outline-none focus:border-[var(--accent)]"
              data-testid="help-chat-input"
            />
            <button onClick={send} disabled={sending}
                    className="w-10 h-10 flex items-center justify-center disabled:opacity-50"
                    style={{ background: "var(--accent)", color: "var(--bg)" }}>
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
